import {
    FilePdfOutlined,
    LockOutlined,
    MailOutlined,
    // ShopOutlined,
    UserOutlined,
    FieldTimeOutlined,
    CheckCircleTwoTone,
} from '@ant-design/icons'
import { Button, Checkbox, Col, Form, Input, Modal, Row } from 'antd'
import React, {
    useEffect,
    useState,
    useRef,
} from 'react'
// import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import {
    Link,
    useSearchParams,
    useLocation,
} from 'react-router-dom'
import Countdown, { zeroPad } from 'react-countdown'

import { useHandler } from './handler_provider'
import logo from './images/TruCarbon_Logo.png'

import { USER_OTP_RENEWAL_TYPES } from './config'
import ServiceAgreement from "./components/ServiceAgreement";
import TermsConditions from "./components/TermsConditions";
import PrivacyPolicy from "./components/PrivacyPolicy";

const COMMON_WEAK_PASSWORDS = new Set([
    'password', 'password1', 'password12', 'password123', 'password1234',
    'passw0rd', 'p@ssword', 'p@ssw0rd',
    'qwerty', 'qwerty1', 'qwerty12', 'qwerty123', 'qwertyuiop',
    '12345678', '123456789', '1234567890', '11111111', '00000000',
    'letmein', 'letmein1', 'welcome', 'welcome1', 'welcome123',
    'admin', 'admin1', 'admin123', 'administrator',
    'iloveyou', 'sunshine', 'monkey1', 'football',
    'trucarbon', 'trucarbon1', 'trucarbon123',
    'abc12345', 'abcdefgh',
])

const containsEmailLocalPart = (pwd, email) => {
    const local = (email || '').split('@')[0].toLowerCase().trim()
    if (local.length < 3) return false
    return pwd.toLowerCase().includes(local)
}

// Checks the SLD (2nd-level domain) of the user's email — e.g. "trucarbon"
// from "shirad@trucarbon.co". Approximates a corporate-name check for
// domain-owned emails without needing an authenticated lookup.
const containsEmailDomain = (pwd, email) => {
    const domain = (email || '').split('@')[1]?.split('.')[0]?.toLowerCase().trim()
    if (!domain || domain.length < 3) return false
    return pwd.toLowerCase().includes(domain)
}

const containsName = (pwd, name) => {
    const n = (name || '').toLowerCase().trim()
    if (n.length < 3) return false
    return pwd.toLowerCase().includes(n)
}

// Detects a run of >= threshold consecutive characters whose codes are all
// ascending (+1), descending (-1), or repeating (0). Catches "1234", "dcba",
// "aaaa", and the same patterns embedded inside longer passwords.
const hasSequentialRun = (str, threshold = 4) => {
    const s = (str || '').toLowerCase()
    if (s.length < threshold) return false
    let runLen = 1
    let runDelta = null
    for (let i = 1; i < s.length; i++) {
        const delta = s.charCodeAt(i) - s.charCodeAt(i - 1)
        if (delta === 1 || delta === -1 || delta === 0) {
            if (delta === runDelta) {
                runLen++
            } else {
                runDelta = delta
                runLen = 2
            }
            if (runLen >= threshold) return true
        } else {
            runDelta = null
            runLen = 1
        }
    }
    return false
}

// 0-4 bucket scoring — rewards length + character variety, penalises
// weak/all-digits/sequential patterns. Purely presentational.
const computePasswordStrength = (pwd) => {
    if (!pwd) return 0
    let score = 0
    if (pwd.length >= 8) score += 1
    if (pwd.length >= 10) score += 1
    if (pwd.length >= 14) score += 1
    if (/[a-z]/.test(pwd)) score += 1
    if (/[A-Z]/.test(pwd)) score += 1
    if (/\d/.test(pwd)) score += 1
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1
    if (COMMON_WEAK_PASSWORDS.has(pwd.toLowerCase())) score = 0
    else if (/^\d+$/.test(pwd)) score = Math.min(score, 1)
    else if (hasSequentialRun(pwd, 4)) score = Math.min(score, 2)
    if (score >= 7) return 4
    if (score >= 5) return 3
    if (score >= 3) return 2
    return 1
}

const PasswordStrengthMeter = ({pwd}) => {
    if (!pwd) return null
    const filled = computePasswordStrength(pwd)
    const labels = ['', 'Weak', 'Fair', 'Good', 'Strong']
    const colors = ['', '#dc3545', '#fd7e14', '#ffc107', '#28a745']
    const color = colors[filled]
    return (
        <div style={{marginTop: 4, marginBottom: 8, fontFamily: 'Muli'}}>
            <div style={{display: 'flex', gap: 4, height: 4}}>
                {[1, 2, 3, 4].map((i) => (
                    <div
                        key={i}
                        style={{
                            flex: 1,
                            background: i <= filled ? color : '#e5e7eb',
                            borderRadius: 2,
                        }}
                    />
                ))}
            </div>
            <small style={{display: 'block', marginTop: 4, color, fontWeight: 500}}>
                {labels[filled]}
            </small>
        </div>
    )
}

function Register() {
    const handler = useHandler()
    const [searchParams] = useSearchParams()
    const location = useLocation()

    const [lexicon, setLexicon] = useState({})
    const [otpSignUpResult, setOtpSignUpResult] = useState(null)

    const [openTC, setOpenTC] = useState(false)
    const [openPP, setOpenPP] = useState(false)
    const [openSA, setOpenSA] = useState(false)

    const inputOtpRef = useRef()
    const countDownRef = useRef()

    const [form] = Form.useForm()
    const [submittable, setSubmittable] = useState(false);

    const [pwd, setPwd] = useState("");
    const meetsLength = pwd.length >= 8;
    const hasUpper = /[A-Z]/.test(pwd);
    const hasSpecial = /[^A-Za-z0-9]/.test(pwd);

    const onFieldsChange = () => {
        const requiredFields = ["email", "firstName", "password", "privacyPolicy", "termsAndConditions", "serviceAgreement"]; // lastName not included

        const allRequiredFilled = requiredFields.every((field) => {
            const value = form.getFieldValue(field);
            return value !== undefined && value !== "";
        });

        const hasErrors = form
            .getFieldsError(requiredFields)
            .some(({ errors }) => errors.length > 0);

        setSubmittable(allRequiredFilled && !hasErrors);
    };

    useEffect(() => {
        handler.subToLexicon(setLexicon)
        return () => handler.unsubFromLexicon(setLexicon)
    }, [handler])

    useEffect(() => {
        const setValidOtpSignUpResult = (result) => {
            result && setOtpSignUpResult(result)
        }
        handler.subToOtpSignUpResult(setValidOtpSignUpResult)
        return () => handler.unsubFromOtpSignUpResult(setValidOtpSignUpResult)
    }, [handler])

    useEffect(() => {
        if (otpSignUpResult && countDownRef && countDownRef.current) {
            countDownRef.current.start()
        }
    }, [otpSignUpResult, countDownRef])

    const toLoginLocation = location.search ? `/login${location.search}` : '/login'

    const renderer = ({ minutes, seconds, completed }) => {
        if (completed) {
            // Render a completed state
            return (
                <div className='d-flex justify-content-between'>
                    <p className="mb-0 text-muted">
                        Didn't get the email?
                        <br />
                        You can request a new code in {zeroPad(minutes)}:{zeroPad(seconds)}
                    </p>
                    <button
                        type="button"
                        className="fw-semibold text-primary text-decoration-underline"
                        aria-disabled={false}
                        onClick={() => {
                            const emailValue = otpSignUpResult?.email
                            if (!emailValue) {
                                return null
                            } else {
                                handler.requestUserOtpRenewal({
                                    email: emailValue,
                                    type: USER_OTP_RENEWAL_TYPES.REGISTER,
                                })
                                form.setFieldsValue({ otp: '' })
                                inputOtpRef?.current?.focus({
                                    cursor: 'start',
                                })
                            }
                        }}
                    >
                        {lexicon.resend}
                    </button>
                </div>
            )
        } else {
            return (
                <div className='d-flex justify-content-between'>
                    <p className="mb-0 text-muted">
                        Didn't get the email?
                        <br />
                        You can request a new code in {zeroPad(minutes)}:{zeroPad(seconds)}
                    </p>                    <button
                        type="button"
                        className="fw-semibold text-primary text-decoration-underline text-muted"
                        aria-disabled={true}
                        disabled={true}
                    >
                        {lexicon.resend}
                    </button>
                </div>
            )
        }
    }

    const Content = (page) => {
        switch (page) {
            case 0:
                return (
                    <>
                        <div className="text-center mt-2 space-y-0">
                            <h5 className="text-primary !text-[#0a564b] font-muli text-2xl font-bold">
                                Welcome to TruCarbon
                            </h5>
                            <p className="text-muted text-base">
                                {lexicon.create_new_account}
                            </p>
                        </div>
                        <div className="p-2 mt-4">
                            <Form
                                onFieldsChange={onFieldsChange}
                                layout="vertical"
                                form={form}
                                name="register"
                                onFinish={(values) => {
                                    handler.requestUserRegistration({
                                        email: values.email,
                                        // name: values.name,
                                        firstName: values.firstName,
                                        lastName: values.lastName,
                                        // phone: `+${phone.phoneCode}${phone.phoneNumber}`,
                                        password: values.password,
                                        serviceAgreement: values.serviceAgreement,
                                    })
                                }}
                                initialValues={{
                                    // residence: ["zhejiang", "hangzhou", "xihu"],
                                    // prefix: "86",
                                    // phone: {
                                    // countryCode: `${countryCode}`
                                    // }
                                }}
                                style={{
                                    maxWidth: 600,
                                }}
                                scrollToFirstError
                            >
                                <Form.Item
                                    name="email"
                                    label={<span className="font-muli font-bold">{lexicon.email}</span>}
                                    rules={[
                                        {
                                            type: 'email',
                                            message: 'Please enter an email that is correctly formatted',
                                        },
                                        {
                                            required: true,
                                            message: 'Please enter your Email',
                                        },
                                    ]}
                                >
                                    <Input
                                        prefix={
                                            <MailOutlined
                                                style={{ color: 'lightGray' }}
                                                className="site-form-item-icon"
                                            />
                                        }
                                        placeholder={lexicon.email}
                                    />
                                </Form.Item>

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="firstName"
                                            label={<span className="font-muli font-bold">{lexicon.first_name}</span>}
                                            rules={[
                                                { required: true, message: "Please enter your First Name" },
                                            ]}
                                        >
                                            <Input
                                                prefix={<UserOutlined style={{ color: "lightGray" }} />}
                                                placeholder={lexicon.first_name}
                                                onChange={(e) => {
                                                    const onlyLetters = e.target.value.replace(/[0-9]/g, "");
                                                    form.setFieldsValue({ firstName: onlyLetters });
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="lastName"
                                            label={<span className="font-muli font-bold">{lexicon.last_name}</span>}
                                        >
                                            <Input
                                                prefix={<UserOutlined style={{ color: "lightGray" }} />}
                                                placeholder={lexicon.last_name}
                                                onChange={(e) => {
                                                    const onlyLetters = e.target.value.replace(/[0-9]/g, "");
                                                    form.setFieldsValue({ lastName: onlyLetters });
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Form.Item
                                    name="password"
                                    label={<span className="font-muli font-bold">{lexicon.password}</span>}
                                    dependencies={['email', 'firstName', 'lastName']}
                                    rules={[
                                        { required: true, message: 'Please enter your password' },
                                        {
                                            validator: (_, value) => {
                                                const v = value || "";
                                                if (v.length < 8) return Promise.reject('');
                                                if (!/[A-Z]/.test(v)) return Promise.reject('');
                                                if (!/[^A-Za-z0-9]/.test(v)) return Promise.reject('');
                                                if (/^\d+$/.test(v)) return Promise.reject('Please avoid using only numbers.');
                                                if (hasSequentialRun(v, 4)) return Promise.reject('Please avoid obvious sequences like 1234 or abcd.');
                                                if (COMMON_WEAK_PASSWORDS.has(v.toLowerCase())) return Promise.reject('This password is too common. Please choose another one.');
                                                if (containsEmailLocalPart(v, form.getFieldValue('email'))) return Promise.reject('Please do not use your email address in your password.');
                                                if (containsEmailDomain(v, form.getFieldValue('email'))) return Promise.reject('Please do not use your company or email domain in your password.');
                                                if (containsName(v, form.getFieldValue('firstName'))) return Promise.reject('Please do not use your name in your password.');
                                                if (containsName(v, form.getFieldValue('lastName'))) return Promise.reject('Please do not use your name in your password.');
                                                return Promise.resolve();
                                            },
                                        },
                                    ]}
                                >
                                    <Input.Password
                                        prefix={<LockOutlined style={{ color: 'lightGray' }}
                                            className="site-form-item-icon" />}
                                        type="password"
                                        placeholder={lexicon.password}
                                        value={pwd}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            setPwd(v);
                                            form.setFieldsValue({ password: v });
                                        }}
                                    />
                                </Form.Item>
                                <PasswordStrengthMeter pwd={pwd}/>
                                <div className="font-muli">
                                    <ul className="space-y-1 text-sm">
                                        <li className={`flex items-center gap-2 ${meetsLength ? "text-green-600" : "text-gray-600"}`}>
                                            {meetsLength
                                                ? <CheckCircleTwoTone twoToneColor="#16a34a" />
                                                :
                                                <span className="w-2 h-2 bg-gray-400 rounded-full inline-block"></span>}
                                            <span>Minimum 8 characters</span>
                                        </li>
                                        <li className={`flex items-center gap-2 ${hasUpper ? "text-green-600" : "text-gray-600"}`}>
                                            {hasUpper
                                                ? <CheckCircleTwoTone twoToneColor="#16a34a" />
                                                :
                                                <span className="w-2 h-2 bg-gray-400 rounded-full inline-block"></span>}
                                            <span>At least 1 capital letter</span>
                                        </li>
                                        <li className={`flex items-center gap-2 ${hasSpecial ? "text-green-600" : "text-gray-600"}`}>
                                            {hasSpecial
                                                ? <CheckCircleTwoTone twoToneColor="#16a34a" />
                                                :
                                                <span className="w-2 h-2 bg-gray-400 rounded-full inline-block"></span>}
                                            <span>At least 1 special character</span>
                                        </li>
                                    </ul>
                                </div>

                                <div className="border-t border-gray-200 my-2"></div>

                                <Form.Item
                                    className='mb-0'
                                    name="termsAndConditions"
                                    valuePropName="checked"
                                    rules={[
                                        {
                                            message: 'Please accept the terms and conditions',
                                            validator: (_, value) =>
                                                value ? Promise.resolve() : Promise.reject('terms and conditions has not been accepted'),
                                        },
                                    ]}
                                    hasFeedback
                                >
                                    <Checkbox>
                                        <span className="font-muli">I agree to the&nbsp;</span>
                                        <span className='underline' onClick={(e) => {
                                            e.preventDefault();
                                            setOpenTC(true)
                                        }}>Terms & Conditions</span>
                                    </Checkbox>
                                </Form.Item>

                                <Form.Item
                                    className='mb-0'
                                    name="privacyPolicy"
                                    valuePropName="checked"
                                    rules={[
                                        {
                                            message: 'Please agree to the privacy policy',
                                            validator: (_, value) =>
                                                value ? Promise.resolve() : Promise.reject('privacy policy has not been agreed to'),
                                        },
                                    ]}
                                    hasFeedback
                                >
                                    <Checkbox>
                                        <span className="font-muli">I agree to the&nbsp;</span>
                                        <span className='underline' onClick={(e) => {
                                            e.preventDefault();
                                            setOpenPP(true)
                                        }}>Privacy Policy</span>
                                    </Checkbox>
                                </Form.Item>

                                <Form.Item
                                    className='mb-0'
                                    name="serviceAgreement"
                                    valuePropName="checked"
                                    rules={[
                                        {
                                            message: 'Please agree to the service agreement',
                                            validator: (_, value) =>
                                                value ? Promise.resolve() : Promise.reject('service agreement has not been agreed to'),
                                        },
                                    ]}
                                    hasFeedback
                                >
                                    <Checkbox>
                                        <span className="font-muli">I agree to the&nbsp;</span>
                                        <span className='underline' onClick={(e) => {
                                            e.preventDefault();
                                            setOpenSA(true)
                                        }}>Service Agreement</span>
                                    </Checkbox>
                                </Form.Item>
                                <div className="border-t border-gray-200 my-2"></div>

                                <Form.Item>
                                    <Button className="bg-[#E17E7F] font-muli"
                                        type="primary"
                                        htmlType="submit"
                                        block
                                        disabled={!submittable}
                                    >
                                        {lexicon.sign_up}
                                    </Button>
                                </Form.Item>
                            </Form>
                            {/* ===== T&C Modal ===== */}
                            <Modal
                                open={openTC}
                                closable={false}
                                maskClosable={false}
                                onCancel={() => setOpenTC(false)}
                                className="[&_.ant-modal-content]:p-0 min-w-[52%]"
                                title={null}
                                footer={null}
                            >
                                <h1 className="p-3 bg-[#0A564B] rounded-t-lg font-semibold text-white">📃 Terms &
                                    Conditions</h1>
                                <div>
                                    <TermsConditions />
                                </div>
                                <div className="p-3 flex flex-row border-t">
                                    <div className="flex flex-row gap-1 items-center font-semibold text-[#0A564B]">
                                        <FilePdfOutlined className="text-2xl" />
                                        <a
                                            className="flex flex-row items-center hover:text-[#0A564B]"
                                            href="/assets/documents/terms-conditions.pdf"
                                            download
                                        >
                                            Download
                                        </a>
                                    </div>
                                    <div className="flex-1" />
                                    <div className="flex flex-row gap-3">
                                        <button
                                            className="px-4 py-1 border-2 border-[#0A564B] rounded-2xl text-[#0A564B] font-semibold"
                                            onClick={() => {
                                                form.setFieldValue("termsAndConditions", false);
                                                setOpenTC(false)
                                            }}
                                        >
                                            Decline
                                        </button>
                                        <button
                                            className="px-4 py-1 bg-[#0A564B] rounded-2xl text-white font-semibold shadow-md shadow-cyan-500/20"
                                            onClick={() => {
                                                form.setFieldValue("termsAndConditions", true);
                                                form.validateFields();
                                                setOpenTC(false)
                                            }}
                                        >
                                            Accept
                                        </button>
                                    </div>
                                </div>
                            </Modal>

                            {/* ===== Privacy Policy Modal ===== */}
                            <Modal
                                open={openPP}
                                closable={false}
                                maskClosable={false}
                                onCancel={() => setOpenPP(false)}
                                className="[&_.ant-modal-content]:p-0 min-w-[52%]"
                                title={null}
                                footer={null}
                            >
                                <h1 className="p-3 bg-[#0A564B] rounded-t-lg font-semibold text-white">📃 Privacy
                                    Policy</h1>
                                <div>
                                    <PrivacyPolicy />
                                </div>
                                <div className="p-3 flex flex-row border-t">
                                    <div className="flex flex-row gap-1 items-center font-semibold text-[#0A564B]">
                                        <FilePdfOutlined className="text-2xl" />
                                        <a
                                            className="flex flex-row items-center hover:text-[#0A564B]"
                                            href="/assets/documents/privacy-policy.pdf"
                                            download
                                        >
                                            Download
                                        </a>
                                    </div>
                                    <div className="flex-1" />
                                    <div className="flex flex-row gap-3">
                                        <button
                                            className="px-4 py-1 border-2 border-[#0A564B] rounded-2xl text-[#0A564B] font-semibold"
                                            onClick={() => {
                                                form.setFieldValue("privacyPolicy", false);
                                                setOpenPP(false)
                                            }}
                                        >
                                            Decline
                                        </button>
                                        <button
                                            className="px-4 py-1 bg-[#0A564B] rounded-2xl text-white font-semibold shadow-md shadow-cyan-500/20"
                                            onClick={() => {
                                                form.setFieldValue("privacyPolicy", true);
                                                form.validateFields();
                                                setOpenPP(false)
                                            }}
                                        >
                                            Accept
                                        </button>
                                    </div>
                                </div>
                            </Modal>

                            {/* ===== Service Agreement Modal ===== */}
                            <Modal
                                open={openSA}
                                closable={false}
                                maskClosable={false}
                                onCancel={() => setOpenSA(false)}
                                className="[&_.ant-modal-content]:p-0 min-w-[52%]"
                                title={null}
                                footer={null}
                            >
                                <h1 className="p-3 bg-[#0A564B] rounded-t-lg font-semibold text-white">📃 Service
                                    Agreement</h1>
                                <div>
                                    <ServiceAgreement />
                                </div>
                                <div className="p-3 flex flex-row border-t">
                                    <div className="flex flex-row gap-1 items-center font-semibold text-[#0A564B]">
                                        <FilePdfOutlined className="text-2xl" />
                                        <a
                                            className="flex flex-row items-center hover:text-[#0A564B]"
                                            href="/assets/documents/service-agreement.pdf"
                                            download
                                        >
                                            Download
                                        </a>
                                    </div>
                                    <div className="flex-1" />
                                    <div className="flex flex-row gap-3">
                                        <button
                                            className="px-4 py-1 border-2 border-[#0A564B] rounded-2xl text-[#0A564B] font-semibold"
                                            onClick={() => {
                                                form.setFieldValue("serviceAgreement", false);
                                                setOpenSA(false)
                                            }}
                                        >
                                            Decline
                                        </button>
                                        <button
                                            className="px-4 py-1 bg-[#0A564B] rounded-2xl text-white font-semibold shadow-md shadow-cyan-500/20"
                                            onClick={() => {
                                                form.setFieldValue("serviceAgreement", true);
                                                form.validateFields();
                                                setOpenSA(false)
                                            }}
                                        >
                                            Accept
                                        </button>
                                    </div>
                                </div>
                            </Modal>
                        </div>
                    </>
                )
            case 1:
                return (
                    <>
                        <div className="text-center mt-2">
                            <h5 className="text-primary text-base">
                                {lexicon.verify_email}
                            </h5>
                            <lord-icon
                                src={'https://cdn.lordicon.com/rhvddzym.json'}
                                trigger="loop"
                                colors="primary:#0A564B"
                                className="avatar-xl"
                                style={{ height: '100px', width: '100px' }}
                            ></lord-icon>
                        </div>

                        <div
                            className="alert alert-borderless alert-warning text-center text-base mb-2 mx-2"
                            role="alert"
                        >
                            <p>{lexicon.please_enter_the_OTP_code_sent_to} {' '}
                                <span className="fw-bold">
                                    {otpSignUpResult?.email}
                                </span>
                            </p>
                        </div>

                        <div className="p-2">
                            <Form
                                layout="vertical"
                                name="register"
                                className="login-form"
                                form={form}
                                onFinish={(values) => {
                                    if (
                                        !otpSignUpResult?.email ||
                                        !values.otp
                                    ) {
                                        return null
                                    } else {
                                        handler.requestUserEmailVerification({
                                            email: otpSignUpResult.email,
                                            otp: values.otp,
                                            ...(searchParams.get('redirect_') && {
                                                redirect_: searchParams.get('redirect_'),
                                            }),
                                            ...(searchParams.get('redirect') && {
                                                redirect: searchParams.get('redirect'),
                                            }),
                                        })
                                    }
                                }}
                            >
                                <Form.Item
                                    label="OTP"
                                    name="otp"
                                    className='mb-2'
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please input your OTP!',
                                        },
                                    ]}
                                >
                                    <Input
                                        prefix={
                                            <FieldTimeOutlined
                                                style={{ color: 'lightGray' }}
                                                className="site-form-item-icon"
                                            />
                                        }
                                        type="text"
                                        placeholder="OTP"
                                        ref={inputOtpRef}
                                    />
                                </Form.Item>
                                <Form.Item>
                                    {otpSignUpResult ? (
                                        <Countdown
                                            key={new Date(otpSignUpResult.otpExpiry)}
                                            ref={countDownRef}
                                            date={new Date(otpSignUpResult.otpExpiry)}
                                            renderer={renderer}
                                            autoStart={false}
                                        >
                                        </Countdown>
                                    ) : (
                                        <></>
                                    )}
                                </Form.Item>

                                <Form.Item>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        className="bg-[#E17E7F] font-muli"
                                        block
                                    >
                                        {lexicon.sign_up}
                                    </Button>
                                </Form.Item>
                            </Form>
                        </div>
                    </>
                )
            default:
                <></>
        }
    }

    return <div className="auth-page-wrapper pt-5">
        <div className="auth-one-bg-position auth-one-bg" id="auth-particles">
            <div className="bg-overlay"></div>

            <div className="shape">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    version="1.1"
                    viewBox="0 0 1440 120"
                >
                    <path d="M 0,36 C 144,53.6 432,123.2 720,124 C 1008,124.8 1296,56.8 1440,40L1440 140L0 140z"></path>
                </svg>
            </div>
        </div>

        <div className="auth-page-content">
            <div className="container">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="text-center mt-sm-5 mb-4 text-white-50">
                            <div>
                                <Link to="/" className="d-inline-block auth-logo">
                                    <img className="max-h-12" src={logo} alt="" height="20" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row justify-content-center">
                    <div className="col-md-8 col-lg-6 col-xl-5">
                        <div className="card mt-4">
                            <div className="card-body p-4">
                                <Content />
                                {Content(!otpSignUpResult ? 0 : 1)}
                            </div>
                        </div>

                        <div className="mt-4 text-center">
                            <p className="mb-0">
                                {lexicon.already_have_an_account}? {' '}
                                <Link
                                    to={toLoginLocation}
                                    className="fw-semibold text-primary text-decoration-underline"
                                >
                                    <span className="font-muli font-bold text-[#E17E7F] underline">{lexicon.sign_in}</span>
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <footer className="footer">
            <div className="container">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="text-center">
                            <p className="mb-0 text-muted">
                                &copy;&nbsp;{new Date().getFullYear()} TruCarbon.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    </div>

}

export default Register
